import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useId, useRef, useState } from 'react';
import { Option } from '~/types';

type SegmentedControlProps = {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  title?: string;
};

export const SegmentedControl = ({
  options,
  value,
  onChange,
  className,
  title,
}: SegmentedControlProps) => {
  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (options.length === 0) {
      return 0;
    }

    const foundIndex = options.findIndex((opt) => opt.value === value);
    return foundIndex === -1 ? 0 : foundIndex;
  });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (options.length === 0) {
      setSelectedIndex(0);
      return;
    }

    const matchIndex = options.findIndex((opt) => opt.value === value);
    if (matchIndex !== -1) {
      setSelectedIndex((prev) => (prev === matchIndex ? prev : matchIndex));
      return;
    }

    setSelectedIndex((prev) => {
      const upperBound = options.length - 1;
      const clamped = Math.min(Math.max(prev, 0), upperBound);
      return clamped;
    });
  }, [value, options]);

  // Update button refs array when options change
  useEffect(() => {
    buttonRefs.current = buttonRefs.current.slice(0, options.length);
  }, [options.length]);

  const handleClick = (option: Option, index: number) => {
    setSelectedIndex(index);
    onChange(option.value);
    // Move focus to the newly selected button
    buttonRefs.current[index]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    option: Option,
    index: number
  ) => {
    let handled = false;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp': {
        event.preventDefault();
        const prevIndex = index === 0 ? options.length - 1 : index - 1;
        setSelectedIndex(prevIndex);
        onChange(options[prevIndex].value);
        buttonRefs.current[prevIndex]?.focus();
        handled = true;
        break;
      }
      case 'ArrowRight':
      case 'ArrowDown': {
        event.preventDefault();
        const nextIndex = index === options.length - 1 ? 0 : index + 1;
        setSelectedIndex(nextIndex);
        onChange(options[nextIndex].value);
        buttonRefs.current[nextIndex]?.focus();
        handled = true;
        break;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        setSelectedIndex(index);
        onChange(option.value);
        handled = true;
        break;
      }
    }

    if (!handled) {
      return;
    }
  };

  const optionsLength = options.length ? options.length : 1;
  const titleId = useId();
  const titleIdValue = title ? titleId : undefined;

  return (
    <div className={className}>
      {title && (
        <label
          id={titleIdValue}
          className='mb-2 block text-sm font-medium text-gray-700'
        >
          {title}
        </label>
      )}
      <div
        ref={containerRef}
        role='radiogroup'
        aria-labelledby={titleIdValue}
        className='relative inline-flex w-full rounded-lg border border-outline-variant bg-white p-1'
      >
        {/* Sliding background indicator */}
        {options.length > 0 && (
          <motion.div
            className='absolute inset-y-1 rounded-md border border-outline-variant bg-brand-ghost'
            initial={false}
            animate={{
              x: `${selectedIndex * 100}%`,
            }}
            transition={{
              type: 'spring',
              stiffness: 300,
              damping: 30,
            }}
            style={{
              width: `calc(${100 / optionsLength}% - 8px)`,
              marginLeft: '4px',
            }}
          />
        )}

        {/* Buttons */}
        {options.map((option, index) => (
          <button
            key={option.value}
            ref={(el) => {
              buttonRefs.current[index] = el;
            }}
            type='button'
            role='radio'
            aria-checked={selectedIndex === index}
            aria-label={option.label}
            tabIndex={selectedIndex === index ? 0 : -1}
            onClick={() => handleClick(option, index)}
            onKeyDown={(e) => handleKeyDown(e, option, index)}
            className={classNames(
              'relative z-10 px-6 py-2.5 font-medium transition-colors duration-200',
              {
                'text-primary': selectedIndex === index,
                'text-secondary hover:text-gray-900': selectedIndex !== index,
              }
            )}
            style={{ width: `${100 / optionsLength}%` }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
};
